import type { PaceBand, PlanSummary } from "../../../lib/trainingPlanApi";

export function paceMinutes(finish: string): number {
  const [h, m] = finish.split(":").map(Number);
  return h * 60 + (m || 0);
}

/** Parse a typed finish time (`H:MM`) into whole minutes. */
export function parseFinishMinutes(input: string): number | null {
  const m = /^(\d+):(\d{2})$/.exec(input.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (min >= 60 || h < 0) return null;
  return h * 60 + min;
}

/** Map common separators (`.`, `,`, space, `-`) to a colon; strip spaces. */
export function normalizeFinishTimeSeparators(raw: string): string {
  return raw
    .trim()
    .replace(/[.,\-–—]/g, ":")
    .replace(/\s+/g, "");
}

/** Keep digits and at most one colon. */
export function sanitizeFinishTimeInput(raw: string): string {
  const normalized = normalizeFinishTimeSeparators(raw);
  let out = "";
  let colons = 0;
  for (const ch of normalized) {
    if (ch >= "0" && ch <= "9") out += ch;
    else if (ch === ":" && colons < 1) {
      out += ch;
      colons++;
    }
  }
  return out;
}

/** Insert `:` before the last two digits when typing digits only (e.g. `308` → `3:08`). */
export function autoFormatFinishTime(input: string): string {
  if (!input) return "";
  if (input.includes(":")) {
    const [hours = "", mins = ""] = input.split(":", 2);
    return mins ? `${hours}:${mins.slice(0, 2)}` : hours ? `${hours}:` : "";
  }
  const digits = input.slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, -2)}:${digits.slice(-2)}`;
}

/** Pad minutes and apply auto-format on blur. */
export function finalizeFinishTimeInput(input: string): string {
  const formatted = autoFormatFinishTime(sanitizeFinishTimeInput(input));
  const m = /^(\d+):(\d{1,2})$/.exec(formatted);
  if (!m) return formatted;
  return `${m[1]}:${m[2].padStart(2, "0")}`;
}

/** Allow in-progress `H:MM` shapes while typing. */
export function isPartialFinishTimeFormat(input: string): boolean {
  if (!input) return true;
  if (input.includes("::")) return false;
  return /^\d+(:\d{0,2})?$/.test(input);
}

/**
 * Map a target finish to the slowest stocked plan band that is still at or
 * faster than the target (e.g. 3:08 → 3:00). Faster than the quickest band
 * clamps to the quickest.
 */
export function roundDownToPlanFinish(targetMinutes: number, bands: PaceBand[]): string {
  if (bands.length === 0) return "";
  let best = bands[0]!;
  for (const b of bands) {
    if (b.goalMinutes <= targetMinutes) best = b;
    else break;
  }
  return best.finish;
}

export function slowestPlanFinish(bands: PaceBand[]): string {
  return bands[bands.length - 1]?.finish ?? bands[0]?.finish ?? "";
}

export type GoalMode = "preset" | "custom" | "just_finish";

/** Nearest plan (same length) to the goal finish time, by goal minutes. */
export function nearestPlan(plans: PlanSummary[], weeks: number, finish: string): PlanSummary | null {
  const target = paceMinutes(finish);
  let best: PlanSummary | null = null;
  let bestD = Infinity;
  for (const p of plans) {
    if (p.weeks !== weeks) continue;
    const d = Math.abs(p.goalMinutes - target);
    if (d < bestD) {
      bestD = d;
      best = p;
    }
  }
  return best;
}
